import { Color, TypographyColor } from "../components/typography"
import React, { useCallback, useId, useState } from "react"
import { Text, types, useAdminContext } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../RedesignTypography"

import { Icon } from "../components/icon"
import { Icons } from "../../types"
import classNames from "classnames"
import { useMediaQuery } from "usehooks-ts"

export interface FaqItemRedesignProps {
    headline: string
    text: TypographyElement[]
    index?: number
    light?: boolean
    setIsOpen: (index?: number | ((isOpen?: number) => number | undefined)) => void
    isOpen: number | undefined
}

const FaqItemRedesign: types.Brick<FaqItemRedesignProps> = ({ index, setIsOpen, isOpen, light }) => {
    const [isOpenMobile, setIsOpenMobile] = useState(index === isOpen)

    const isMobile = useMediaQuery("(max-width: 767px)")

    const open = useCallback(
        (index?: number) => (isMobile ? isOpenMobile : isOpen === index),
        [isMobile, isOpen, isOpenMobile]
    )

    const triggerId = useId()
    const contentId = useId()

    const { isAdmin } = useAdminContext()

    return (
        <li
            className={classNames("border-b py-5 last:border-b-0 sm:py-6 first:pt-0 last:pb-0", {
                "border-white/30": light,
                "border-border-tide": !light,
            })}
        >
            <button
                type="button"
                onClick={() => {
                    setIsOpen((isOpen?: number) => (index === isOpen ? undefined : index))
                    setIsOpenMobile(isOpenMobile => !isOpenMobile)
                }}
                className="flex w-full justify-between items-start gap-5 text-left"
                id={triggerId}
                aria-expanded={open(index)}
                aria-controls={contentId}
            >
                <Text
                    propName="headline"
                    placeholder="Titel"
                    renderBlock={({ children }) => (
                        <span
                            className={classNames("font-gibson text-md font-semibold sm:text-lg lg:text-xl", {
                                "text-white": light,
                                "text-dark-grey": !light,
                            })}
                        >
                            {children}
                        </span>
                    )}
                />
                <Icon
                    icon={Icons.arrow_down}
                    className={classNames("shrink-0 text-base sm:text-md lg:text-lg leading-none transition duration-300 ease-in-out mt-1", {
                        "rotate-180": open(index),
                        "text-white": light,
                        "text-dark-grey": !light,
                    })}
                />
            </button>
            <div
                className={classNames(
                    "grid transition-grid-template-rows duration-300 ease-out",
                    open(index) ? "grid-rows-1fr" : "grid-rows-0fr"
                )}
                id={contentId}
                aria-labelledby={triggerId}
                aria-hidden={!open(index)}
            >
                <div className={classNames({ "overflow-hidden": !(isAdmin && open(index)) })}>
                    <div className="pt-4">
                        <Color color={light ? TypographyColor.White : undefined}>
                            <Typography
                                size="P2"
                                allowList={["bold", "sup", "link", "small", "ul"]}
                                propName="text"
                                hidden={!open(index)}
                                className="text-md"
                            />
                        </Color>
                    </div>
                </div>
            </div>
        </li>
    )
}

FaqItemRedesign.schema = {
    name: "FaqItemRedesign",
    label: "FAQ Item Redesign",
    hideFromAddMenu: true,
    getDefaultProps: (): Omit<FaqItemRedesignProps, "setIsOpen" | "isOpen" | "index"> => ({
        headline: "Lorem ipsum dolor sit amet",
        text: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed euismod, nunc ut laoreet tincidunt, nunc nisl aliquam nunc, eget aliquam nisl nunc eu nunc. Sed euismod, nunc ut laoreet tincidunt, nunc nisl aliquam nunc, eget aliquam nisl nunc eu nunc.",
                    },
                ],
            },
        ],
    }),
}

export default FaqItemRedesign
